import { useField } from '@react-aria/label';
import { FocusEvent, ReactElement, useId, useState } from 'react';

import { DownArrow, ErrorContainer, InnerWrapper, Label, Select, Wrapper } from './styles';
import { FormSelectProps } from './types';

const FormSelect = ({
	name,
	value,
	placeholder,
	label,
	onChange,
	onBlur,
	isValid: _isValid,
	noValidation,
	backgroundColor,
	textColor,
	options,
	className,
	disabled,
	LabelComponent,
	$UNSAFE_hasBlurred: UNSAFE_hasBlurred = false,
	...props
}: FormSelectProps): ReactElement => {
	const isValid = _isValid ?? true;
	const error = props.error ?? props.errors?.[0];
	const [hasBlurred, setHasBlurred] = useState(UNSAFE_hasBlurred);
	const handleBlur = ((e: FocusEvent<HTMLSelectElement>): void => {
		if (!disabled) {
			setHasBlurred(true);
		}
		if (onBlur) {
			onBlur(e);
		}
	}) as (e: FocusEvent) => void;
	const hasValue = Boolean(value);
	const showValidation =
		props.showValidation ?? (hasBlurred && !noValidation && typeof _isValid !== 'undefined');
	const showErrorMessage = showValidation && !isValid && error;
	const errorMessageId = useId();
	const { labelProps, fieldProps, errorMessageProps } = useField({
		label,
		'aria-label': props['aria-label'],
		'aria-labelledby': props['aria-labelledby'],
		...(noValidation ? {} : { validationState: isValid ? 'valid' : 'invalid' }),
		...(showErrorMessage ? { errorMessge: error, ['aria-errormessage']: errorMessageId } : {}),
	});

	const showDefaultLabel = Boolean(label && !LabelComponent);
	return (
		<>
			{LabelComponent ? <LabelComponent {...{ ...labelProps, children: label }} /> : null}
			<Wrapper className={className}>
				<InnerWrapper>
					{showDefaultLabel && (
						<Label $isDisabled={disabled} $isFocusedOrHasValue $textColor={textColor} {...labelProps}>
							{label}
						</Label>
					)}
					<DownArrow $hasValue={hasValue} />
					<Select
						id={fieldProps.id}
						name={name}
						value={value}
						$hasValue={hasValue}
						$backgroundColor={backgroundColor}
						$textColor={textColor}
						$provideSpaceForAnimatedLabel={showDefaultLabel}
						{...(showValidation ? { $isValid: isValid, $isError: !isValid } : {})}
						aria-errormessage={showErrorMessage ? errorMessageId : undefined}
						aria-invalid={showErrorMessage ? 'true' : 'false'}
						onChange={onChange}
						onBlur={handleBlur}
						disabled={disabled}
						{...props}
						{...fieldProps}
					>
						{placeholder && (
							<option value="" disabled hidden>
								{placeholder}
							</option>
						)}
						{options.map(optionName =>
							typeof optionName === 'string' ? (
								<option key={optionName} value={optionName}>
									{optionName}
								</option>
							) : (
								<option key={optionName.value} value={optionName.value} disabled={optionName.disabled}>
									{optionName.label}
								</option>
							),
						)}
					</Select>
				</InnerWrapper>
				{showErrorMessage && (
					<ErrorContainer
						{...errorMessageProps}
						id={errorMessageId}
						role="alert"
						aria-live="polite"
						tabIndex={0}
						aria-describedby={fieldProps.id}
					>
						{error}
					</ErrorContainer>
				)}
			</Wrapper>
		</>
	);
};

export default FormSelect;
